import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileIcon } from '../../components/icons/ChatCategoryIcons.js';
import {
  deleteInspectorRecord,
  getInspectorHistory,
  getInspectorHistoryExportData,
  getInspectorRecord,
  updateBatchInspectorStudentName,
  updateInspectorRecordMeta,
} from '../../api/inspectorApi';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';
import { calculateNEISBytes } from '../../utils/neisBytes';
import { trackEvent } from '../../utils/eventTracker';
import { filterLocatedInspectorViolations, guidelinePageLabel } from '../../utils/inspectorViolations';
import { getInspectorGuidelineContextLabel } from './InspectorGuidelineContextSelector';
import * as XLSX from 'xlsx';
import './SaenggibuHistory.css';
import './SaenggibuInspector.css';
import './InspectorHistory.css';

// ── 카테고리별 색상 (검사기와 동일) ──
const CATEGORY_COLORS = {
  ending: '#3b82f6',
  brand: '#dc2626',
  institution: '#ea580c',
  expression: '#ca8a04',
  spelling: '#0891b2',
  word: '#7c3aed',
  other: '#6b7280',
};

// ── 위반 심각도 정의 ──
const VIOLATION_SEVERITY = {
  '브랜드명/상표명': 4,
  '브랜드명 기재 금지': 4,
  '브랜드/상호명 기재 금지': 4,
  '교육기관명': 4,
  '특정 기관/대회명 기재 금지': 4,
  '외모/신체': 4,
  '가정환경': 4,
  '건강/질병 정보': 4,
  '종교 관련': 4,
  '비교/서열화': 4,
  '부정적 표현': 4,
  '금지 표현': 4,
  '자소서식 표현': 3,
  '잘못된 어미': 3,
  '종결어미': 3,
  '모호한 표현': 2,
  '구체성 부족': 2,
  '추측성 표현': 2,
  '객관성 부족': 2,
  '중복/오류': 1,
  '맞춤법 오류': 1,
  '오탈자': 1
};

const SEVERITY_COLORS = {
  4: '#dc2626',
  3: '#ea580c',
  2: '#ca8a04',
  1: '#2563eb'
};

// ── 모드 필터 탭 ──
const FILTER_TABS = [
  { key: '전체',   label: '전체' },
  { key: 'single', label: '단일' },
  { key: 'batch',  label: '일괄' },
];

const HISTORY_PAGE_SIZE = 200;
const MAX_LOADED_HISTORY_RECORDS = 1000;
const MAX_HISTORY_EXPORT_RECORDS = 100;
const MAX_HISTORY_EXPORT_ROWS = 1000;

const estimateInspectorExportRows = (records) => records.reduce((sum, record) => {
  if (record?.mode === 'batch') {
    return sum + Math.max(1, Number(record.total_students || record.results?.length || 0));
  }
  return sum + 1;
}, 0);

const getAllViolations = (target = {}) => {
  const violations = [
    ...(target.basic_violations || []).map(v => ({ ...v, source: 'basic' })),
    ...(target.guideline_violations || []).map(v => ({ ...v, source: 'guideline' }))
  ];
  const originalText = target.content ?? target.input_text ?? '';
  return originalText
    ? filterLocatedInspectorViolations(violations, originalText)
    : violations;
};

const getViolationCount = (target = {}) => getAllViolations(target).length;

const summarizeViolations = (target = {}) => {
  const violations = getAllViolations(target);
  if (violations.length === 0) return '위반 없음';
  return violations
    .map(v => `${v.type || '개선 권장'}${v.found ? `: ${v.found}` : ''}`)
    .join('\n');
};

// ── 날짜 그룹 헬퍼 ──
const getDateGroup = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  if (date >= today) return '오늘';
  if (date >= yesterday) return '어제';
  if (date >= weekAgo) return '이번 주';
  if (date >= monthStart) return '이번 달';
  return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' });
};

// ── 년도/학기 필터 헬퍼 ──
const getCalendarYear = (dateStr) => new Date(dateStr).getFullYear();

const isInSemesterRange = (dateStr, year, semester) => {
  const date = new Date(dateStr);
  if (semester === 1) {
    const start = new Date(year, 2, 1);
    const end = new Date(year, 8, 0, 23, 59, 59);
    return date >= start && date <= end;
  } else {
    const start = new Date(year, 8, 1);
    const end = new Date(year + 1, 2, 0, 23, 59, 59);
    return date >= start && date <= end;
  }
};

const InspectorHistory = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expandedIds, setExpandedIds] = useState([]);
  const [isDownloadingExcel, setIsDownloadingExcel] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState('전체');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('전체');
  const [selectedSemester, setSelectedSemester] = useState('전체');

  // 위반사항 상세 패널용 상태
  const [selectedViolation, setSelectedViolation] = useState(null);
  const [activeRecordId, setActiveRecordId] = useState(null);
  const [activeStudentIdxByRecord, setActiveStudentIdxByRecord] = useState({});
  const [loadingDetailIds, setLoadingDetailIds] = useState([]);
  const [editingRecordTitleId, setEditingRecordTitleId] = useState(null);
  const [editingRecordTitleValue, setEditingRecordTitleValue] = useState('');
  const [savingRecordTitleId, setSavingRecordTitleId] = useState(null);
  const [editingBatchStudentName, setEditingBatchStudentName] = useState(null);
  const [editingBatchStudentNameValue, setEditingBatchStudentNameValue] = useState('');
  const [savingBatchStudentName, setSavingBatchStudentName] = useState(null);
  const detailPanelRef = useRef(null);
  const violationSelectTimerRef = useRef(null);

  useEffect(() => () => {
    if (violationSelectTimerRef.current) {
      clearTimeout(violationSelectTimerRef.current);
    }
  }, []);

  // ── 날짜 포맷 (간결하게) ──
  const formatDate = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (d >= today) {
      return d.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
    }
    if (d.getFullYear() !== now.getFullYear()) {
      return d.toLocaleDateString('ko-KR', { year: 'numeric', month: 'numeric', day: 'numeric' });
    }
    return d.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
  };

  // ── 사용 가능한 년도 목록 ──
  const availableYears = useMemo(() => {
    const yearSet = new Set();
    historyData.forEach(r => yearSet.add(getCalendarYear(r.created_at)));
    return Array.from(yearSet).sort((a, b) => b - a);
  }, [historyData]);

  // ── 1단계: 년도/학기 + 검색 (최상위) ──
  const baseFilteredData = useMemo(() => {
    let data = historyData;
    if (selectedAcademicYear !== '전체') {
      const yearNum = parseInt(selectedAcademicYear, 10);
      if (selectedSemester === '전체') {
        data = data.filter(r => getCalendarYear(r.created_at) === yearNum);
      } else {
        const semNum = parseInt(selectedSemester, 10);
        data = data.filter(r => isInSemesterRange(r.created_at, yearNum, semNum));
      }
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      data = data.filter(r => {
        if ((r.custom_title || '').toLowerCase().includes(q)) return true;
        if (r.mode === 'single') {
          return (r.input_text || '').toLowerCase().includes(q) ||
                 (r.preview || '').toLowerCase().includes(q) ||
                 (r.summary || '').toLowerCase().includes(q) ||
                 (r.refined_sentence || '').toLowerCase().includes(q);
        }
        if (r.mode === 'batch' && r.results) {
          return r.results.some(s =>
            (s.student_name || '').toLowerCase().includes(q) ||
            (s.content || '').toLowerCase().includes(q) ||
            (s.refined_sentence || '').toLowerCase().includes(q) ||
            summarizeViolations(s).toLowerCase().includes(q)
          );
        }
        return false;
      });
    }
    return data;
  }, [historyData, selectedAcademicYear, selectedSemester, searchQuery]);

  // ── 모드별 개수 (baseFilteredData 기반) ──
  const modeCounts = useMemo(() => {
    const counts = { '전체': baseFilteredData.length, 'single': 0, 'batch': 0 };
    baseFilteredData.forEach(r => {
      if (r.mode === 'single') counts['single']++;
      else if (r.mode === 'batch') counts['batch']++;
    });
    return counts;
  }, [baseFilteredData]);

  // ── 2단계: 모드 필터 ──
  const filteredData = useMemo(() => {
    let data = baseFilteredData;
    if (selectedFilter !== '전체') {
      data = data.filter(r => r.mode === selectedFilter);
    }
    return data;
  }, [baseFilteredData, selectedFilter]);

  // ── 날짜 그룹핑 ──
  const groupedData = useMemo(() => {
    const groups = [];
    let currentGroup = null;
    filteredData.forEach(record => {
      const group = getDateGroup(record.created_at);
      if (group !== currentGroup) {
        groups.push({ type: 'header', label: group });
        currentGroup = group;
      }
      groups.push({ type: 'record', data: record });
    });
    return groups;
  }, [filteredData]);

  // ── 데이터 로드 ──
  const loadHistory = useCallback(async (page = 1, append = false) => {
    try {
      setIsLoading(!append);
      const offset = (page - 1) * HISTORY_PAGE_SIZE;
      const response = await getInspectorHistory(HISTORY_PAGE_SIZE, offset);
      const newData = response.records || [];
      if (append) {
        setHistoryData(prev => [...prev, ...newData]);
      } else {
        setHistoryData(newData);
      }
      setHasMore(Boolean(response.has_more) && (offset + newData.length) < MAX_LOADED_HISTORY_RECORDS);
    } catch (error) {
      console.error('히스토리 로드 실패:', error);
      showToast('기록을 불러오지 못했어요');
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    if (currentUser) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // ── 더보기 ──
  const handleLoadMore = useCallback(() => {
    if (historyData.length >= MAX_LOADED_HISTORY_RECORDS) {
      setHasMore(false);
      showToast(`화면에서는 최대 ${MAX_LOADED_HISTORY_RECORDS.toLocaleString()}개 기록까지만 불러올 수 있어요.`, 'warning', 6000);
      return;
    }
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    loadHistory(nextPage, true);
  }, [currentPage, historyData.length, loadHistory, showToast]);

  // ── 체크박스 ──
  const handleToggleSelect = useCallback((id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    const targetIds = filteredData.map(r => r.id);
    const allSelected = targetIds.length > 0 && targetIds.every(id => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : targetIds);
  }, [filteredData, selectedIds]);

  // ── 삭제 ──
  const handleOpenDeleteConfirm = useCallback(() => {
    if (selectedIds.length === 0) { showToast('삭제할 기록을 선택해주세요'); return; }
    setShowDeleteConfirm(true);
  }, [selectedIds, showToast]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.length === 0) return;
    const idsToDelete = [...selectedIds];
    const count = idsToDelete.length;
    const prevData = historyData;

    // 낙관적 업데이트: UI 즉시 반영
    setHistoryData(prev => prev.filter(r => !idsToDelete.includes(r.id)));
    setSelectedIds([]);
    setShowDeleteConfirm(false);
    showToast(`${count}개 기록을 삭제했어요`);

    try {
      await Promise.all(idsToDelete.map(id => deleteInspectorRecord(id)));
    } catch (error) {
      console.error('기록 삭제 실패:', error);
      setHistoryData(prevData);
      showToast('삭제 중 문제가 생겼어요. 다시 시도해주세요');
    }
  }, [selectedIds, historyData, showToast]);

  // ── 카드 확장/축소 ──
  const toggleExpand = useCallback(async (recordOrId) => {
    const record = typeof recordOrId === 'object' ? recordOrId : null;
    const recordId = record?.id || recordOrId;
    if (violationSelectTimerRef.current) {
      clearTimeout(violationSelectTimerRef.current);
      violationSelectTimerRef.current = null;
    }
    setSelectedViolation(null);
    if (expandedIds.includes(recordId)) {
      setExpandedIds(prev => prev.filter(id => id !== recordId));
      if (activeRecordId === recordId) {
        setActiveRecordId(null);
      }
    } else {
      setActiveRecordId(recordId);
      const needsHydratedDetail = (
        record?.mode === 'batch'
        || (
          record?.mode === 'single'
          && (
            record.original_payload_backed
            || record.input_text_cache_truncated
          )
        )
      );
      if (needsHydratedDetail) {
        // 먼저 확장해서 스켈레톤을 보여주고, 상세는 백그라운드에서 로드
        setExpandedIds(prev => [...prev, recordId]);
        if (record?.mode === 'batch') {
          setActiveStudentIdxByRecord(prev => ({
            ...prev,
            [recordId]: prev[recordId] ?? 0,
          }));
        }
        setLoadingDetailIds(prev => prev.includes(recordId) ? prev : [...prev, recordId]);
        try {
          const response = await getInspectorRecord(recordId);
          const detailRecord = response.record || response.data || response || record;
          setHistoryData(prev => prev.map(item => item.id === recordId ? { ...item, ...detailRecord } : item));
        } catch (error) {
          console.error('검사 기록 상세 로드 실패:', error);
          showToast('상세 정보를 불러오지 못했어요');
          setExpandedIds(prev => prev.filter(id => id !== recordId));
        } finally {
          setLoadingDetailIds(prev => prev.filter(id => id !== recordId));
        }
        return;
      }

      setExpandedIds(prev => [...prev, recordId]);
      if (record?.mode === 'batch' && record.results?.length > 0) {
        setActiveStudentIdxByRecord(prev => ({ ...prev, [recordId]: prev[recordId] ?? 0 }));
      }
    }
  }, [activeRecordId, expandedIds, showToast]);

  // ── 복사 ──
  // tracking: 교사 반응 측정용 이벤트 값(기록 id·학생 순번·복사한 내용 종류만, 본문·이름 없음)
  const handleCopy = async (text, label = '내용', tracking = null) => {
    if (!text) {
      showToast('저장된 내용이 없어요', 'warning');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label}을 복사했어요`);
      if (tracking) trackEvent('inspector_history_copy', tracking);
    } catch { showToast('복사에 실패했어요'); }
  };

  const historyCopyTracking = (record, studentIndex, contentType) => ({
    record_id: record?.id || null,
    mode: record?.mode === 'batch' ? 'batch' : 'single',
    student_index: record?.mode === 'batch' ? (studentIndex ?? null) : null,
    school_level: record?.school_level || null,
    content_type: contentType,
  });

  const handleOpenInInspector = useCallback(async (record, studentIndex = 0) => {
    let resolvedRecord = record;
    if (
      record.id
      && (
        !record.academic_year
        || record.original_payload_backed
        || record.input_text_cache_truncated
        || record.mode === 'batch'
      )
    ) {
      try {
        const response = await getInspectorRecord(record.id);
        resolvedRecord = response.record || response.data || response || record;
        setHistoryData(prev => prev.map(item => (
          item.id === record.id ? { ...item, ...resolvedRecord } : item
        )));
      } catch (error) {
        console.error('검사 기준 상세 조회 실패:', error);
        showToast('검사 원문을 불러오지 못했어요. 잠시 후 다시 시도해주세요', 'warning', 6000);
        return;
      }
    }
    const student = resolvedRecord.mode === 'batch'
      ? resolvedRecord.results?.[studentIndex]
      : null;
    const reusableText = (
      student?.content
      || student?.refined_sentence
      || resolvedRecord.input_text
      || resolvedRecord.refined_sentence
    );
    if (!reusableText) {
      showToast('이 기록에는 이어서 수정할 초안이 없어요', 'warning', 6000);
      return;
    }
    navigate('/saenggibu-inspector', {
      state: {
        restoreFromRecord: true,
        record: resolvedRecord,
        studentIndex,
      }
    });
  }, [navigate, showToast]);

  const getInspectorTitle = (record, options = {}) => {
    if (!options.ignoreCustom && record.custom_title?.trim()) {
      return record.custom_title.trim();
    }
    if (record.mode === 'batch') {
      const total = record.total_students || record.results?.length || 0;
      return total > 0 ? `일괄 검사 · ${total}명` : '일괄 검사';
    }
    const text = (record.preview || record.input_text || record.summary || record.refined_sentence || '').replace(/\n+/g, ' ').trim();
    return text.length > 72 ? text.substring(0, 72) + '...' : (text || '검사 기록');
  };

  const getRecordContextLabel = (record, includeMissing = false) => {
    const years = (record.academic_years || [])
      .map(Number)
      .filter(Boolean)
      .sort((a, b) => a - b);
    if (record.context_mode === 'mixed' && years.length > 0) {
      const yearLabel = years.length === 1
        ? `${years[0]}학년도`
        : `${years[0]}~${years[years.length - 1]}학년도`;
      return `${yearLabel} · 여러 학년`;
    }
    if (record.academic_year) {
      return getInspectorGuidelineContextLabel(
        record.academic_year,
        record.record_grade,
      );
    }
    return includeMissing ? '적용 기준 미기록' : '';
  };

  const getInspectorSubtitle = (record) => {
    const contextLabel = getRecordContextLabel(record);
    if (record.custom_title?.trim()) {
      const generatedTitle = getInspectorTitle(record, { ignoreCustom: true });
      if (record.mode === 'batch') {
        const {
          perfectStudents: perfect,
          problemStudents: problem,
        } = getBatchDisplaySummary(record);
        return [generatedTitle, `완벽 ${perfect}명`, `문제 ${problem}명`, contextLabel]
          .filter(Boolean)
          .join(' · ');
      }
      const text = (record.preview || record.input_text || record.summary || record.refined_sentence || '').replace(/\n+/g, ' ').trim();
      const preview = text.length > 50 ? `${text.substring(0, 50)}...` : text;
      return [preview, contextLabel].filter(Boolean).join(' · ');
    }
    if (record.mode === 'batch') {
      const {
        perfectStudents: perfect,
        problemStudents: problem,
      } = getBatchDisplaySummary(record);
      return [
        `완벽 ${perfect}명`,
        `문제 ${problem}명`,
        getModelLabel(record.selected_model),
        contextLabel,
      ].filter(Boolean).join(' · ');
    }
    const items = [
      `${record.bytes || 0}B`,
      getModelLabel(record.selected_model),
      contextLabel,
    ];
    if (record.refined_sentence) items.push('AI 수정안 있음');
    return items.filter(Boolean).join(' · ');
  };

  const getModelLabel = (model) => model === 'gemini-2.5-flash-lite' ? '빠른 모델' : '고급 모델';

  // 레거시 기록에 total_violations 필드가 없어도 위반 배열로 폴백 계산
  const getRecordViolationCount = (record) => {
    if (record.mode === 'single' && record.input_text) {
      return getViolationCount(record);
    }
    if (
      record.mode === 'batch'
      && record.results?.some(student => student?.content)
    ) {
      return record.results.reduce(
        (sum, student) => sum + getViolationCount(student),
        0,
      );
    }
    const direct = Number(record.total_violations);
    if (Number.isFinite(direct) && direct > 0) return direct;
    if (record.mode === 'single') return getViolationCount(record);
    if (record.results?.length > 0) {
      return record.results.reduce((sum, student) => sum + getViolationCount(student), 0);
    }
    return 0;
  };

  const getBatchDisplaySummary = (record) => {
    if (record.results?.length > 0) {
      const successful = record.results.filter(student => !student?.error);
      const problemStudents = successful.filter(
        student => getViolationCount(student) > 0,
      ).length;
      return {
        perfectStudents: successful.length - problemStudents,
        problemStudents,
        failedStudents: record.results.length - successful.length,
      };
    }
    return {
      perfectStudents: record.summary?.perfect_students || 0,
      problemStudents: record.summary?.students_with_violations || 0,
      failedStudents: record.summary?.failed_checks || 0,
    };
  };

  const renderInspectorStatus = (record) => {
    const count = getRecordViolationCount(record);
    return (
      <span className={`ih-status-badge ${count > 0 ? 'ih-status-badge--violation' : 'ih-status-badge--pass'}`}>
        {count > 0 ? `위반 ${count}` : '통과'}
      </span>
    );
  };

  const getSchoolLevelLabel = (schoolLevel) => {
    const labels = {
      elementary: '초등',
      middle: '중등',
      high: '고등',
    };
    return labels[schoolLevel] || schoolLevel || '';
  };

  const getExpandedMetaItems = (record, baseItems = []) => {
    const violationCount = getRecordViolationCount(record);
    return [
      ...baseItems,
      getSchoolLevelLabel(record.school_level),
      getRecordContextLabel(record, true),
      getModelLabel(record.selected_model),
      record.revision_history?.length > 0 ? `${record.revision_history.length}회 다듬음` : '',
      violationCount > 0 ? `위반 ${violationCount}개` : '위반 없음',
    ].filter(Boolean);
  };

  const renderExpandedMeta = (items) => (
    <div className="sh-expanded-meta">
      {items.map((item, index) => (
        <React.Fragment key={`${item}-${index}`}>
          {index > 0 && <span className="sh-expanded-meta-sep">·</span>}
          <span>{item}</span>
        </React.Fragment>
      ))}
    </div>
  );

  const handleStartRecordTitleEdit = (record) => {
    setEditingRecordTitleId(record.id);
    setEditingRecordTitleValue(record.custom_title?.trim() || getInspectorTitle(record, { ignoreCustom: true }));
  };

  const handleCancelRecordTitleEdit = () => {
    setEditingRecordTitleId(null);
    setEditingRecordTitleValue('');
  };

  const handleSaveRecordTitleEdit = async (recordId) => {
    const nextTitle = editingRecordTitleValue.trim();
    const previousData = historyData;
    setSavingRecordTitleId(recordId);
    setHistoryData(prev => prev.map(record => (
      record.id === recordId ? { ...record, custom_title: nextTitle } : record
    )));

    try {
      const response = await updateInspectorRecordMeta(recordId, { custom_title: nextTitle });
      setHistoryData(prev => prev.map(record => (
        record.id === recordId ? { ...record, custom_title: response.custom_title || nextTitle } : record
      )));
      showToast(nextTitle ? '기록 이름을 저장했어요' : '기록 이름을 비웠어요');
      setEditingRecordTitleId(null);
      setEditingRecordTitleValue('');
    } catch (error) {
      console.error('검사 기록 이름 저장 실패:', error);
      setHistoryData(previousData);
      showToast('기록 이름을 저장하지 못했어요. 다시 시도해주세요', 'error');
    } finally {
      setSavingRecordTitleId(null);
    }
  };

  const updateBatchStudentNameInHistory = useCallback((recordId, studentIndex, studentName) => {
    setHistoryData(prev => prev.map(record => {
      if (record.id !== recordId) return record;
      const results = [...(record.results || [])];
      const target = results[studentIndex];
      if (!target) return record;
      results[studentIndex] = {
        ...target,
        student_name: studentName,
      };
      return { ...record, results };
    }));
  }, []);

  const handleStartBatchStudentNameEdit = useCallback((recordId, studentIndex, currentName) => {
    setEditingBatchStudentName({ recordId, studentIndex });
    setEditingBatchStudentNameValue(currentName || '');
  }, []);

  const handleCancelBatchStudentNameEdit = useCallback(() => {
    setEditingBatchStudentName(null);
    setEditingBatchStudentNameValue('');
  }, []);

  const handleSaveBatchStudentNameEdit = useCallback(async () => {
    if (!editingBatchStudentName) return;
    const { recordId, studentIndex } = editingBatchStudentName;
    const nextName = editingBatchStudentNameValue.trim();
    if (!nextName) {
      showToast('학생 이름을 입력해주세요', 'warning');
      return;
    }

    const previousData = historyData;
    setSavingBatchStudentName({ recordId, studentIndex });
    updateBatchStudentNameInHistory(recordId, studentIndex, nextName);

    try {
      const response = await updateBatchInspectorStudentName(recordId, studentIndex, nextName);
      updateBatchStudentNameInHistory(recordId, studentIndex, response.student_name || nextName);
      showToast('학생 이름을 저장했어요', 'success');
      setEditingBatchStudentName(null);
      setEditingBatchStudentNameValue('');
    } catch (error) {
      console.error('일괄 검사 학생 이름 저장 실패:', error);
      setHistoryData(previousData);
      showToast('학생 이름을 저장하지 못했어요. 다시 시도해주세요', 'error');
    } finally {
      setSavingBatchStudentName(null);
    }
  }, [
    editingBatchStudentName,
    editingBatchStudentNameValue,
    historyData,
    showToast,
    updateBatchStudentNameInHistory,
  ]);

  // ── 엑셀 다운로드 ──
  const handleExcelDownload = useCallback(async () => {
    const targetData = selectedIds.length > 0
      ? historyData.filter(r => selectedIds.includes(r.id))
      : filteredData;

    if (targetData.length === 0) { showToast('다운로드할 기록이 없어요'); return; }
    if (targetData.length > MAX_HISTORY_EXPORT_RECORDS) {
      showToast(`한 번에 최대 ${MAX_HISTORY_EXPORT_RECORDS}개 기록까지만 다운로드할 수 있어요. 범위를 나눠 선택해주세요.`, 'warning', 8000);
      return;
    }

    const estimatedRows = estimateInspectorExportRows(targetData);
    if (estimatedRows > MAX_HISTORY_EXPORT_ROWS) {
      showToast(`예상 ${estimatedRows.toLocaleString()}행으로 너무 커요. 학생 수나 기록 범위를 나눠 선택해주세요.`, 'warning', 8000);
      return;
    }

    let fd = targetData;

    try {
      setIsDownloadingExcel(true);
      showToast('엑셀을 준비하고 있어요...');

      const recordsNeedingDetail = fd.filter(record => (
        record.mode === 'batch'
        || (
          record.mode === 'single'
          && (
            record.original_payload_backed
            || record.input_text_cache_truncated
          )
        )
      ));
      if (recordsNeedingDetail.length > 0) {
        const exportResponse = await getInspectorHistoryExportData(recordsNeedingDetail.map(record => record.id));
        const detailById = new Map();
        (exportResponse.records || []).forEach((detailRecord) => {
          if (detailRecord?.id) detailById.set(detailRecord.id, detailRecord);
        });
        if (detailById.size > 0) {
          fd = fd.map(record => detailById.has(record.id) ? { ...record, ...detailById.get(record.id) } : record);
          setHistoryData(prev => prev.map(record => detailById.has(record.id) ? { ...record, ...detailById.get(record.id) } : record));
        }
      }

      const excelData = [];
      fd.forEach((record) => {
        if (record.mode === 'single') {
          const refinedBytes = record.refined_sentence ? calculateNEISBytes(record.refined_sentence) : 0;
          const violations = getViolationCount(record);
          excelData.push({
            구분: '단일 검사',
            '작성 학년도': record.academic_year || '미기록',
            '작성 당시 학년': record.record_grade || '미기록',
            '검사 초안': record.input_text || '',
            '초안 바이트': record.bytes || 0,
            '위반 개수': violations,
            '위반 요약': summarizeViolations(record),
            'AI 개선 제안': record.refined_sentence || '없음',
            '개선 바이트': refinedBytes
          });
        } else {
          const results = record.results || [];
          results.forEach((student) => {
            const refinedBytes = student.refined_sentence ? calculateNEISBytes(student.refined_sentence) : 0;
            const violations = getViolationCount(student);
            excelData.push({
              구분: '다중 검사',
              '작성 학년도': student.academic_year || record.academic_year || '미기록',
              '작성 당시 학년': student.record_grade || record.record_grade || '미기록',
              학생명: student.student_name || '',
              '검사 초안': student.content || '',
              '초안 바이트': student.bytes || 0,
              '위반 개수': violations,
              '위반 요약': summarizeViolations(student),
              'AI 개선 제안': student.refined_sentence || '없음',
              '개선 바이트': refinedBytes
            });
          });
        }
      });

      const ws = XLSX.utils.json_to_sheet(excelData);
      ws['!cols'] = [
        { wch: 12 },
        { wch: 14 },
        { wch: 14 },
        { wch: 12 },
        { wch: 80 },
        { wch: 12 },
        { wch: 10 },
        { wch: 60 },
        { wch: 80 },
        { wch: 12 }
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '검사 기록');
      XLSX.writeFile(wb, `생기부검사_기록_${new Date().toISOString().split('T')[0]}.xlsx`);
      trackEvent('inspector_excel_download', {
        source: 'history',
        record_count: fd.length,
        record_ids: fd.slice(0, 50).map(record => record.id),
      });
      showToast(`${fd.length}개 기록이 다운로드 폴더에 저장되었어요`, 'success', 10000);
    } catch { showToast('다운로드 중 문제가 생겼어요. 다시 시도해주세요'); }
    finally { setIsDownloadingExcel(false); }
  }, [historyData, filteredData, selectedIds, showToast]);

  // ── 위반사항 색상 결정 ──
  const getViolationColor = useCallback((violations) => {
    const withCategory = violations.find(v => v.category && CATEGORY_COLORS[v.category]);
    if (withCategory) return CATEGORY_COLORS[withCategory.category];
    const maxSeverity = Math.max(...violations.map(v => VIOLATION_SEVERITY[v.type] || 0));
    return SEVERITY_COLORS[maxSeverity] || '#3b82f6';
  }, []);

  // ── 하이라이트된 텍스트 렌더링 ──
  const renderHighlightedText = useCallback((text, basicViolations = [], guidelineViolations = [], recordId) => {
    const storedViolations = [
      ...(basicViolations || []).map(v => ({ ...v, source: 'basic' })),
      ...(guidelineViolations || []).map(v => ({ ...v, source: 'guideline' }))
    ];
    const allViolations = text
      ? filterLocatedInspectorViolations(storedViolations, text)
      : storedViolations;

    if (!text) {
      return (
        <div className="ih-original-discarded">
          <p>이 기록에는 저장된 검사 초안이 없어요. 새로 검사하면 초안이 기록에 함께 저장돼요.</p>
          {allViolations.length === 0 ? (
            <span className="ih-no-violations">위반사항 없음</span>
          ) : (
            <div className="ih-saved-violation-list">
              {allViolations.map((violation, index) => (
                <button
                  key={`${violation.type || 'violation'}-${index}`}
                  type="button"
                  className="ih-saved-violation"
                  onClick={(event) => {
                    event.stopPropagation();
                    setActiveRecordId(recordId);
                    setSelectedViolation({
                      isGroup: true,
                      found: violation.found || '(저장된 초안 없음)',
                      violations: [violation],
                      count: 1,
                    });
                  }}
                >
                  <strong>{violation.type || '개선 권장'}</strong>
                  <span>{violation.found || violation.reason || violation.rule || '상세 내용을 확인하세요'}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (allViolations.length === 0) {
      return (
        <div>
          <span className="ih-no-violations">위반사항 없음</span>
          <div style={{ lineHeight: '1.8', whiteSpace: 'pre-wrap', marginTop: '8px' }}>{text}</div>
        </div>
      );
    }

    // 그룹화 (빈 found 제외)
    const groupedViolations = {};
    allViolations.forEach(v => {
      if (!v.found) return;
      if (!groupedViolations[v.found]) groupedViolations[v.found] = [];
      groupedViolations[v.found].push(v);
    });

    // 모든 등장 위치 찾기
    const allOccurrences = [];
    Object.entries(groupedViolations).forEach(([found, violations]) => {
      let searchStart = 0;
      while (true) {
        const foundIndex = text.indexOf(found, searchStart);
        if (foundIndex === -1) break;
        allOccurrences.push({ found, violations, index: foundIndex, endIndex: foundIndex + found.length });
        searchStart = foundIndex + 1;
      }
    });

    allOccurrences.sort((a, b) => a.index - b.index);

    // 겹치는 영역 병합
    const mergedOccurrences = [];
    let currentMerged = null;
    allOccurrences.forEach((occurrence) => {
      if (!currentMerged) {
        currentMerged = { ...occurrence, violations: [...occurrence.violations] };
      } else if (occurrence.index <= currentMerged.endIndex) {
        currentMerged.endIndex = Math.max(currentMerged.endIndex, occurrence.endIndex);
        if (occurrence.found.length > currentMerged.found.length) currentMerged.found = occurrence.found;
        currentMerged.violations.push(...occurrence.violations);
      } else {
        mergedOccurrences.push(currentMerged);
        currentMerged = { ...occurrence, violations: [...occurrence.violations] };
      }
    });
    if (currentMerged) mergedOccurrences.push(currentMerged);

    let lastIndex = 0;
    const parts = [];

    mergedOccurrences.forEach((occurrence, idx) => {
      if (occurrence.index > lastIndex) {
        parts.push(<span key={`text-${idx}`}>{text.substring(lastIndex, occurrence.index)}</span>);
      }

      const color = getViolationColor(occurrence.violations);
      const isSelected = selectedViolation &&
        activeRecordId === recordId &&
        selectedViolation.found === occurrence.found &&
        selectedViolation.index === occurrence.index;

      parts.push(
        <span
          key={`violation-${idx}`}
          className={`inspector-highlight ${isSelected ? 'selected' : ''}`}
          style={{ backgroundColor: color, opacity: isSelected ? 1 : 0.6, position: 'relative', display: 'inline-block' }}
          onClick={(e) => {
            e.stopPropagation();
            setActiveRecordId(recordId);
            setSelectedViolation({
              isGroup: true, found: occurrence.found, index: occurrence.index,
              violations: occurrence.violations, count: occurrence.violations.length
            });
            setTimeout(() => {
              if (detailPanelRef.current && window.innerWidth <= 768) {
                detailPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          }}
          title={`${occurrence.violations.length}개 위반: ${occurrence.violations.map(v => v.type).join(', ')}`}
        >
          {occurrence.found}
          {occurrence.violations.length > 1 && (
            <span className="inspector-violation-badge">{occurrence.violations.length}</span>
          )}
        </span>
      );
      lastIndex = occurrence.endIndex;
    });

    if (lastIndex < text.length) {
      parts.push(<span key="text-end">{text.substring(lastIndex)}</span>);
    }

    return (
      <div>
        <div className="inspector-text-display">{parts}</div>
      </div>
    );
  }, [getViolationColor, selectedViolation, activeRecordId]);

  // ── 상세 패널 렌더링 ──
  const renderDetailPanel = useCallback(() => {
    if (!selectedViolation || !activeRecordId) return null;

    return (
      <div ref={detailPanelRef} className="ih-detail-panel">
        <div className="ih-detail-header">
          <h4 className="ih-detail-title">위반사항 상세 ({selectedViolation.count || 1}개)</h4>
          <button className="ih-detail-close" onClick={(e) => { e.stopPropagation(); setSelectedViolation(null); }}>
            닫기
          </button>
        </div>

        {/* 발견된 표현 */}
        <div className="ih-detail-found">
          <div className="ih-detail-found-label">발견된 표현</div>
          <div className="ih-detail-found-text">
            &ldquo;{/^\s+$/.test(selectedViolation.found)
              ? selectedViolation.found.replace(/ /g, '□').replace(/\n/g, '↵')
              : selectedViolation.found}&rdquo;
          </div>
        </div>

        {/* 위반사항 리스트 */}
        <div className="ih-detail-list">
          {selectedViolation.violations?.map((violation, index) => {
            const violationColor = violation.category && CATEGORY_COLORS[violation.category]
              ? CATEGORY_COLORS[violation.category]
              : SEVERITY_COLORS[VIOLATION_SEVERITY[violation.type] || 0] || '#6b7280';

            return (
              <div key={index} className="ih-detail-item">
                <span className="ih-detail-type-badge" style={{ backgroundColor: violationColor }}>
                  {violation.type}
                </span>

                {(violation.reason || violation.rule) && (
                  <div className="ih-detail-reason-block">
                    <strong className="ih-detail-reason-label">
                      {violation.reason ? '개선 이유' : '참고 사항'}
                    </strong>
                    <p className="ih-detail-reason-text">{violation.reason || violation.rule}</p>
                  </div>
                )}

                {violation.file && (
                  <div className="ih-detail-source">
                    <strong>출처:</strong> {violation.file}
                    {guidelinePageLabel(violation.page)}
                  </div>
                )}

                {violation.quote && (
                  <div className="ih-detail-reason-block">
                    <strong className="ih-detail-reason-label">기재요령 원문</strong>
                    <p className="ih-detail-quote">&ldquo;{violation.quote}&rdquo;</p>
                  </div>
                )}

                {violation.suggestion !== undefined && (
                  <div className="ih-detail-reason-block">
                    <strong className="ih-detail-reason-label">수정 제안</strong>
                    <p className="ih-detail-suggestion">
                      {violation.suggestion === ''
                        ? '(삭제)'
                        : /^\s+$/.test(violation.suggestion)
                          ? violation.suggestion.replace(/ /g, '□').replace(/\n/g, '↵')
                          : violation.suggestion}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [selectedViolation, activeRecordId]);

  // ── 학생 결과 선택 핸들러 ──
  const handleSelectStudent = useCallback((record, studentIndex) => {
    if (violationSelectTimerRef.current) {
      clearTimeout(violationSelectTimerRef.current);
      violationSelectTimerRef.current = null;
    }
    setActiveRecordId(record.id);
    setActiveStudentIdxByRecord(prev => ({ ...prev, [record.id]: studentIndex }));
    setSelectedViolation(null);

    const student = record.results?.[studentIndex];
    if (student) {
      const studentText = student.content || student.refined_sentence || '';
      const allViolations = filterLocatedInspectorViolations([
        ...(student.basic_violations || []).map(v => ({ ...v, source: 'basic' })),
        ...(student.guideline_violations || []).map(v => ({ ...v, source: 'guideline' }))
      ], studentText);

      if (allViolations.length > 0) {
        const grouped = {};
        allViolations.forEach(v => {
          if (!v.found) return;
          if (!grouped[v.found]) grouped[v.found] = [];
          grouped[v.found].push(v);
        });

        const occurrences = [];
        Object.entries(grouped).forEach(([found, violations]) => {
          let searchStart = 0;
          while (true) {
            const foundIndex = studentText.indexOf(found, searchStart);
            if (foundIndex === -1) break;
            occurrences.push({ found, violations, index: foundIndex, endIndex: foundIndex + found.length });
            searchStart = foundIndex + 1;
          }
        });

        occurrences.sort((a, b) => a.index - b.index);
        if (occurrences.length > 0) {
          const first = occurrences[0];
          violationSelectTimerRef.current = setTimeout(() => {
            violationSelectTimerRef.current = null;
            setSelectedViolation({
              isGroup: true, found: first.found, index: first.index,
              violations: first.violations, count: first.violations.length
            });
          }, 100);
        }
      }
    }
  }, []);

  // ── 로그인 필요 ──
  if (!currentUser) {
    return (
      <div className="sh-empty">
        <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
        <h3 className="sh-empty-title">로그인이 필요해요</h3>
        <p className="sh-empty-desc">검사 기록을 보려면 먼저 로그인해주세요.</p>
        <button className="sh-empty-btn" onClick={() => navigate('/auth?tab=login&redirect=/inspector-history')}>
          로그인하기
        </button>
      </div>
    );
  }

  return (
    <div className="sh-container">
      {/* 헤더 */}
      <div className="sh-header">
        <div className="sh-header-row">
          <h2 className="sh-title">
            검사 기록
            {historyData.length > 0 && (
              <span className="sh-title-count">
                {selectedIds.length > 0 ? `${selectedIds.length}개 선택` : `${filteredData.length}개`}
              </span>
            )}
          </h2>
        </div>
      </div>

      {/* 검색 + 년도/학기 필터 */}
      {historyData.length > 0 && (
        <div className="sh-search-row">
          <div className="sh-search-wrap">
            <svg className="sh-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="sh-search-input"
              type="text"
              placeholder="초안, 학생명, 수정 제안으로 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="sh-search-clear" onClick={() => setSearchQuery('')}>&times;</button>
            )}
          </div>
          {availableYears.length > 0 && (
            <div className="sh-semester-selects">
              <select
                className={`sh-select ${selectedAcademicYear !== '전체' ? 'sh-select--active' : ''}`}
                value={selectedAcademicYear}
                onChange={(e) => { setSelectedAcademicYear(e.target.value); setSelectedSemester('전체'); }}
              >
                <option value="전체">전체 검사 시기</option>
                {availableYears.map(y => (
                  <option key={y} value={String(y)}>{y}년 검사</option>
                ))}
              </select>
              {selectedAcademicYear !== '전체' && (
                <select
                  className={`sh-select ${selectedSemester !== '전체' ? 'sh-select--active' : ''}`}
                  value={selectedSemester}
                  onChange={(e) => setSelectedSemester(e.target.value)}
                >
                  <option value="전체">전체 학기</option>
                  <option value="1">1학기</option>
                  <option value="2">2학기</option>
                </select>
              )}
            </div>
          )}
        </div>
      )}

      {/* 모드 필터 탭 */}
      {!isLoading && historyData.length > 0 && (
        <div className="sh-filter-tabs">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              className={`sh-filter-tab ${selectedFilter === tab.key ? 'sh-filter-tab--active' : ''}`}
              onClick={() => { setSelectedFilter(tab.key); setSelectedIds([]); }}
            >
              {tab.label}
              <span className="sh-filter-tab-count">{modeCounts[tab.key]}</span>
            </button>
          ))}
        </div>
      )}

      {/* 검색 결과 안내 */}
      {searchQuery.trim() && (
        <div className="sh-search-result">
          &lsquo;{searchQuery.trim()}&rsquo; 검색 결과 {filteredData.length}개
        </div>
      )}

      {/* 액션 바 */}
      {!isLoading && filteredData.length > 0 && (
        <div className="sh-actions">
          <button className="sh-action-btn sh-action-btn--select" onClick={handleToggleSelectAll}>
            {filteredData.length > 0 && filteredData.every(r => selectedIds.includes(r.id)) ? '선택 해제' : '전체 선택'}
          </button>
          {selectedIds.length > 0 && (
            <button className="sh-action-btn sh-action-btn--delete" onClick={handleOpenDeleteConfirm}>
              {`삭제 (${selectedIds.length})`}
            </button>
          )}
          <button className="sh-action-btn sh-action-btn--excel" onClick={handleExcelDownload} disabled={isDownloadingExcel}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {isDownloadingExcel ? '준비 중...' : (selectedIds.length > 0 ? `다운로드 (${selectedIds.length})` : '엑셀')}
          </button>
        </div>
      )}

      {/* 스켈레톤 로딩 */}
      {isLoading && historyData.length === 0 && (
        <div className="sh-list">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="sh-skeleton-card">
              <div className="sh-skeleton-line sh-skeleton-line--medium" />
              <div className="sh-skeleton-line sh-skeleton-line--short" />
            </div>
          ))}
        </div>
      )}

      {/* 빈 상태 */}
      {!isLoading && historyData.length === 0 && (
        <div className="sh-empty">
          <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
          <h3 className="sh-empty-title">아직 기록이 없어요</h3>
          <p className="sh-empty-desc">생기부를 검사하면 여기에 저장돼요.</p>
          <button className="sh-empty-btn" onClick={() => window.location.href = '/saenggibu-inspector'}>
            검사하러 가기
          </button>
        </div>
      )}

      {/* 필터된 결과 없음 */}
      {!isLoading && historyData.length > 0 && filteredData.length === 0 && (
        <div className="sh-empty">
          <div className="sh-empty-icon"><FileIcon size={28} color="#9ca3af" /></div>
          <h3 className="sh-empty-title">
            {FILTER_TABS.find(t => t.key === selectedFilter)?.label || selectedFilter} 기록이 없어요
          </h3>
          <p className="sh-empty-desc">AI로 빠르게 검사해볼 수 있어요.</p>
          <button className="sh-empty-btn" onClick={() => window.location.href = '/saenggibu-inspector'}>
            검사하러 가기
          </button>
        </div>
      )}

      {/* 카드 리스트 (날짜 그룹핑) */}
      {filteredData.length > 0 && (
        <div className="sh-list">
          {groupedData.map((item, idx) => {
            if (item.type === 'header') {
              return <div key={`h-${idx}`} className="sh-date-group">{item.label}</div>;
            }
            const record = item.data;
            const isExpanded = expandedIds.includes(record.id);
            const isSelected = selectedIds.includes(record.id);

            return (
              <div
                key={record.id}
                className={`sh-card ${isSelected ? 'sh-card--selected' : ''}`}
              >
                <div
                  className="sh-card-body"
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => toggleExpand(record)}
                  onKeyDown={(e) => {
                    // 내부 버튼(저장/취소/펼치기 등)의 키 입력까지 잡으면 버튼 동작이 막히므로 카드 자신만 처리
                    if (e.target !== e.currentTarget) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleExpand(record);
                    }
                  }}
                >
                  <div
                    className={`sh-checkbox ${isSelected ? 'sh-checkbox--checked' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleToggleSelect(record.id); }}
                  >
                    {isSelected && (
                      <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                        <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>
                  <div className={`sh-card-initial ih-card-initial--${record.mode === 'batch' ? 'batch' : 'single'}`}>
                    {record.mode === 'batch' ? '일' : '검'}
                  </div>
                  <div className="sh-card-info">
                    <div className="sh-card-title-row">
                      {editingRecordTitleId === record.id ? (
                        <span className="sh-record-title-edit" onClick={(e) => e.stopPropagation()}>
                          <input
                            className="sh-record-title-input"
                            value={editingRecordTitleValue}
                            onChange={(e) => setEditingRecordTitleValue(e.target.value)}
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === 'Enter') handleSaveRecordTitleEdit(record.id);
                              if (e.key === 'Escape') handleCancelRecordTitleEdit();
                            }}
                            placeholder="예: 2반 홍길동 검사"
                            maxLength={80}
                            autoFocus
                          />
                          <button
                            type="button"
                            className="sh-record-title-action sh-record-title-action--save"
                            disabled={savingRecordTitleId === record.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSaveRecordTitleEdit(record.id);
                            }}
                          >
                            저장
                          </button>
                          <button
                            type="button"
                            className="sh-record-title-action"
                            disabled={savingRecordTitleId === record.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelRecordTitleEdit();
                            }}
                          >
                            취소
                          </button>
                        </span>
                      ) : (
                        <>
                          <p className="sh-card-title">{getInspectorTitle(record)}</p>
                          <button
                            type="button"
                            className="sh-record-title-edit-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartRecordTitleEdit(record);
                            }}
                            title="기록 이름 수정"
                            aria-label="기록 이름 수정"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                    <p className="sh-card-subtitle">{getInspectorSubtitle(record)}</p>
                  </div>
                  <div className="sh-card-right">
                    <span className="sh-card-date">{formatDate(record.created_at)}</span>
                    <span className="ih-card-status-row">
                      {renderInspectorStatus(record)}
                      {record.refined_sentence && (
                        <span className="ih-card-refined-mark" aria-label="AI 수정안 있음">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6L9 17l-5-5" />
                          </svg>
                        </span>
                      )}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`sh-card-toggle ${isExpanded ? 'sh-card-toggle--open' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleExpand(record);
                    }}
                    aria-label={isExpanded ? '검사 기록 접기' : '검사 기록 펼치기'}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                </div>

                {/* ── 확장 영역 ── */}
                {isExpanded && (
                  <div className="ih-result-section">
                    {record.mode === 'single'
                      ? renderExpandedMeta(getExpandedMetaItems(record, ['단일 검사', `${record.bytes || 0}B`]))
                      : renderExpandedMeta(getExpandedMetaItems(record, [`일괄 ${record.total_students || record.results?.length || 0}명`]))}
                    {loadingDetailIds.includes(record.id) && !(record.results?.length > 0) ? (
                      <div className="ih-detail-skeleton" aria-label="상세 정보를 불러오는 중">
                        <div className="sh-skeleton-line sh-skeleton-line--medium" />
                        <div className="sh-skeleton-line sh-skeleton-line--short" />
                      </div>
                    ) : record.mode === 'single' ? (
                      /* 단일 검사 상세 */
                      <div>
                        <div className="ih-result-header">
                          <div className="ih-result-header-left">
                            <span className="ih-result-label">검사 결과</span>
                            <span className="sh-badge sh-badge--bytes">{record.bytes}B</span>
                          </div>
                          {record.input_text && (
                            <div className="ih-action-group">
                              <button className="ih-copy-btn ih-copy-btn--primary" onClick={(e) => { e.stopPropagation(); handleOpenInInspector(record); }}>
                                검사 화면에서 수정
                              </button>
                              <button className="ih-copy-btn" onClick={(e) => { e.stopPropagation(); handleCopy(record.input_text, '검사 초안', historyCopyTracking(record, null, 'draft')); }}>
                                복사
                              </button>
                            </div>
                          )}
                        </div>

                        {record.input_text && getViolationCount(record) > 0 && (
                          <div className="ih-guide-hint">색칠된 부분을 클릭하면 상세 정보를 볼 수 있어요</div>
                        )}

                        <div className="ih-text-area">
                          {renderHighlightedText(record.input_text, record.basic_violations, record.guideline_violations, record.id)}
                        </div>

                        {activeRecordId === record.id && selectedViolation && renderDetailPanel()}

                        {record.refined_sentence && (
                          <div className="ih-refined">
                            <div className="ih-refined-header">
                              <div className="ih-refined-header-left">
                                <span className="ih-refined-label">AI 수정 제안</span>
                                <span className="ih-refined-bytes">{calculateNEISBytes(record.refined_sentence)}B</span>
                              </div>
                              <div className="ih-action-group">
                                <button className="ih-refined-copy-btn" onClick={(e) => { e.stopPropagation(); handleOpenInInspector(record); }}>
                                  이어서 수정
                                </button>
                                <button className="ih-refined-copy-btn" onClick={(e) => { e.stopPropagation(); handleCopy(record.refined_sentence, 'AI 개선 제안', historyCopyTracking(record, null, 'refined')); }}>
                                  복사
                                </button>
                              </div>
                            </div>
                            <div className="ih-refined-content">{record.refined_sentence}</div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* 일괄 검사 상세 */
                      <div>
                        {(() => {
                          const batchSummary = getBatchDisplaySummary(record);
                          return (
                            <div className="ih-batch-overview">
                              <span>완벽 {batchSummary.perfectStudents}명</span>
                              <span>문제 {batchSummary.problemStudents}명</span>
                              {batchSummary.failedStudents > 0 && (
                                <span>실패 {batchSummary.failedStudents}명</span>
                              )}
                              <span>위반 총 {getRecordViolationCount(record)}개</span>
                            </div>
                          );
                        })()}
                        {/* 학생 선택 탭 */}
                        <div className="ih-student-section">
                          <p className="ih-student-label">학생 선택 ({record.total_students}명)</p>
                          <div className="ih-student-tabs">
                            {record.results?.map((student, sIdx) => {
                              const violations = getViolationCount(student);
                              const activeStudentIndex = activeStudentIdxByRecord[record.id] ?? 0;
                              const isStudentActive = activeStudentIndex === sIdx;

                              return (
                                <button
                                  key={sIdx}
                                  className={`ih-student-tab ${isStudentActive ? 'ih-student-tab--active' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); handleSelectStudent(record, sIdx); }}
                                >
                                  {sIdx + 1}. {student.student_name}
                                  {student.error ? (
                                    <span className="ih-student-badge">실패</span>
                                  ) : (
                                    violations > 0 && <span className="ih-student-badge">{violations}</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {(() => {
                          const activeStudentIndex = activeStudentIdxByRecord[record.id] ?? 0;
                          const activeStudent = record.results?.[activeStudentIndex];
                          if (!activeStudent) {
                            return <div className="ih-student-hint">학생 결과를 불러오고 있어요.</div>;
                          }
                          return (
                            <div>
                              <div className="ih-result-header">
                                <div className="ih-result-header-left">
                                  <span className="ih-result-label">
                                    {editingBatchStudentName?.recordId === record.id && editingBatchStudentName?.studentIndex === activeStudentIndex ? (
                                      <span className="sh-student-name-edit" onClick={(e) => e.stopPropagation()}>
                                        <input
                                          className="sh-student-name-input"
                                          value={editingBatchStudentNameValue}
                                          onChange={(e) => setEditingBatchStudentNameValue(e.target.value)}
                                          onKeyDown={(e) => {
                                            e.stopPropagation();
                                            if (e.key === 'Enter') handleSaveBatchStudentNameEdit();
                                            if (e.key === 'Escape') handleCancelBatchStudentNameEdit();
                                          }}
                                          placeholder="학생 이름"
                                          maxLength={40}
                                          autoFocus
                                        />
                                        <button
                                          type="button"
                                          className="sh-student-name-action sh-student-name-action--save"
                                          disabled={savingBatchStudentName?.recordId === record.id && savingBatchStudentName?.studentIndex === activeStudentIndex}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleSaveBatchStudentNameEdit();
                                          }}
                                        >
                                          저장
                                        </button>
                                        <button
                                          type="button"
                                          className="sh-student-name-action"
                                          disabled={savingBatchStudentName?.recordId === record.id && savingBatchStudentName?.studentIndex === activeStudentIndex}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCancelBatchStudentNameEdit();
                                          }}
                                        >
                                          취소
                                        </button>
                                      </span>
                                    ) : (
                                      <span className="sh-student-name-display">
                                        {activeStudent.student_name}
                                        <button
                                          type="button"
                                          className="sh-student-name-edit-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleStartBatchStudentNameEdit(record.id, activeStudentIndex, activeStudent.student_name);
                                          }}
                                          title="학생 이름 수정"
                                          aria-label={`${activeStudent.student_name || '학생'} 이름 수정`}
                                        >
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                                          </svg>
                                        </button>
                                      </span>
                                    )}
                                  </span>
                                  <span className="sh-badge sh-badge--bytes">
                                    {activeStudent.bytes || calculateNEISBytes(activeStudent.content || activeStudent.refined_sentence || '')}B
                                  </span>
                                </div>
                                {activeStudent.content && (
                                  <div className="ih-action-group">
                                    <button className="ih-copy-btn ih-copy-btn--primary" onClick={(e) => { e.stopPropagation(); handleOpenInInspector(record, activeStudentIndex); }}>
                                      검사 화면에서 수정
                                    </button>
                                    <button className="ih-copy-btn" onClick={(e) => { e.stopPropagation(); handleCopy(activeStudent.content, '검사 초안', historyCopyTracking(record, activeStudentIndex, 'draft')); }}>
                                      복사
                                    </button>
                                  </div>
                                )}
                              </div>

                              {activeStudent.content && !activeStudent.error
                                && getViolationCount(activeStudent) > 0 && (
                                <div className="ih-guide-hint">색칠된 부분을 클릭하면 상세 정보를 볼 수 있어요</div>
                              )}

                              {activeStudent.error && (
                                <div className="ih-inspection-error">
                                  {activeStudent.error_message
                                    || '검사를 완료하지 못했어요. 검사 화면에서 다시 시도해주세요.'}
                                </div>
                              )}

                              <div className="ih-text-area">
                                {renderHighlightedText(
                                  activeStudent.content,
                                  activeStudent.basic_violations,
                                  activeStudent.guideline_violations,
                                  record.id
                                )}
                              </div>

                              {activeRecordId === record.id && selectedViolation && renderDetailPanel()}

                              {activeStudent.refined_sentence && (
                                <div className="ih-refined">
                                  <div className="ih-refined-header">
                                    <div className="ih-refined-header-left">
                                      <span className="ih-refined-label">AI 수정 제안</span>
                                      <span className="ih-refined-bytes">{calculateNEISBytes(activeStudent.refined_sentence)}B</span>
                                    </div>
                                    <div className="ih-action-group">
                                      <button className="ih-refined-copy-btn" onClick={(e) => { e.stopPropagation(); handleOpenInInspector(record, activeStudentIndex); }}>
                                        이어서 수정
                                      </button>
                                      <button className="ih-refined-copy-btn" onClick={(e) => { e.stopPropagation(); handleCopy(activeStudent.refined_sentence, 'AI 개선 제안', historyCopyTracking(record, activeStudentIndex, 'refined')); }}>
                                        복사
                                      </button>
                                    </div>
                                  </div>
                                  <div className="ih-refined-content">{activeStudent.refined_sentence}</div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                )}

              </div>
            );
          })}
          {hasMore && historyData.length >= HISTORY_PAGE_SIZE && historyData.length < MAX_LOADED_HISTORY_RECORDS && (
            <button className="sh-load-more" onClick={handleLoadMore} disabled={isLoading}>
              {isLoading ? '불러오는 중...' : '더보기'}
            </button>
          )}
        </div>
      )}

      {/* 삭제 확인 모달 - UX Writing: 쉬운 말, 해요체 */}
      {showDeleteConfirm && (
        <div className="sh-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="sh-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="sh-modal-title">
              {selectedIds.length === 1 ? '이 기록을 삭제할까요?' : `${selectedIds.length}개 기록을 삭제할까요?`}
            </h3>
            <p className="sh-modal-desc">삭제하면 다시 복구할 수 없어요.</p>
            <div className="sh-modal-actions">
              <button className="sh-modal-btn sh-modal-btn--cancel" onClick={() => setShowDeleteConfirm(false)}>
                취소
              </button>
              <button className="sh-modal-btn sh-modal-btn--delete" onClick={handleDeleteSelected}>
                삭제하기
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default InspectorHistory;
